import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import { useTranslation } from 'react-i18next';
import * as v from 'valibot';
import { X, Loader2, ChevronDown } from 'lucide-react';
import { INDUSTRY_SECTORS } from '@clareefai/types';
import { Avatar, toast, ImageUploadWithCrop } from '@clareefai/ui';
import { uploadFile } from '../../utils/upload';
import { useUpdatePromoterMutation } from '../../store/promoterApi';
import { useLogoSuggestion } from '../shared/useLogoSuggestion';
import LogoSuggestionCard from '../shared/LogoSuggestionCard';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '+1000'] as const;

const editPromoterSchema = v.object({
  firstName: v.pipe(v.string(), v.minLength(2, 'Min 2 characters'), v.maxLength(50, 'Max 50 characters')),
  lastName: v.pipe(v.string(), v.minLength(2, 'Min 2 characters'), v.maxLength(50, 'Max 50 characters')),
  phone: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  company: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  linkedin: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  companyUrl: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  avatar: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  companyLogo: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  companySize: v.optional(v.union([v.picklist(COMPANY_SIZES), v.literal('')])),
  industrySectors: v.optional(v.array(v.string())),
  timezone: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  referenceCallFrequency: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
});

type EditPromoterFormData = v.InferOutput<typeof editPromoterSchema>;

interface EditPromoterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  promoter: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    company?: string;
    linkedin?: string;
    companyUrl?: string;
    avatar?: string;
    companyLogo?: string;
    companySize?: string;
    industrySectors?: string[];
    timezone?: string;
    referenceCallFrequency?: string;
  };
}

const EditPromoterModal: React.FC<EditPromoterModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  promoter,
}) => {
  const { t } = useTranslation();
  const [updatePromoter, { isLoading }] = useUpdatePromoterMutation();
  const [sectorsOpen, setSectorsOpen] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<EditPromoterFormData>({
    resolver: valibotResolver(editPromoterSchema),
    defaultValues: {
      firstName: promoter.firstName,
      lastName: promoter.lastName,
      phone: promoter.phone || '',
      company: promoter.company || '',
      linkedin: promoter.linkedin || '',
      companyUrl: promoter.companyUrl || '',
      avatar: promoter.avatar || '',
      companyLogo: promoter.companyLogo || '',
      companySize: (promoter.companySize as EditPromoterFormData['companySize']) || '',
      industrySectors: promoter.industrySectors || [],
      timezone: promoter.timezone || '',
      referenceCallFrequency: promoter.referenceCallFrequency || '',
    },
  });

  const selectedSectors = watch('industrySectors') || [];

  const logoSuggestion = useLogoSuggestion(
    watch('companyLogo') || '',
    (logo) => setValue('companyLogo', logo, { shouldDirty: true }),
  );

  const toggleSector = (sector: string) => {
    const current = selectedSectors;
    const updated = current.includes(sector)
      ? current.filter((s) => s !== sector)
      : [...current, sector];
    setValue('industrySectors', updated, { shouldDirty: true });
  };

  const onSubmit = async (data: EditPromoterFormData) => {
    try {
      const body: Record<string, any> = {
        first_name: data.firstName,
        last_name: data.lastName,
      };
      if (data.phone !== undefined) body.phone = data.phone || null;
      if (data.company !== undefined) body.company = data.company || null;
      if (data.linkedin !== undefined) body.linkedin = data.linkedin || null;
      if (data.companyUrl !== undefined) body.company_url = data.companyUrl || null;
      // Empty picture falls back to the company logo when one exists, else null.
      body.avatar =
        (data.avatar && data.avatar.trim()) ||
        (data.companyLogo && data.companyLogo.trim()) ||
        null;
      if (data.companyLogo !== undefined) body.company_logo = data.companyLogo || null;
      if (data.companySize !== undefined) body.company_size = data.companySize || null;
      if (data.industrySectors !== undefined) body.industry_sectors = data.industrySectors;
      if (data.timezone !== undefined) body.timezone = data.timezone || null;
      if (data.referenceCallFrequency !== undefined) body.reference_call_frequency = data.referenceCallFrequency || null;

      await updatePromoter({ id: promoter.id, data: body }).unwrap();
      toast.success(t('promoters.editModal.updateSuccess'));
      onSuccess();
    } catch (err: any) {
      toast.error(err?.data?.message || t('promoters.editModal.updateFailed'));
    }
  };

  if (!isOpen) return null;

  const inputClass = (hasError?: boolean) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] ${
      hasError ? 'border-red-300' : 'border-gray-300'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={isLoading ? undefined : onClose} />
      <div className="relative z-50 w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{t('promoters.editModal.title')}</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
            <input
              type="email"
              value={promoter.email}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
            />
            <p className="text-xs text-gray-400 mt-1">{t('promoters.editModal.emailCannotChange')}</p>
          </div>

          <div className="flex flex-col items-center gap-2">
            <label className="block text-sm font-medium text-gray-700">{t('promoters.form.picture')}</label>
            <ImageUploadWithCrop
              value={watch('avatar') || ''}
              onChange={(url) => setValue('avatar', url, { shouldDirty: true })}
              onUpload={(blob) => uploadFile(blob, 'avatars')}
              shape="round"
              aspect={1}
            />
            <p className="text-xs text-gray-500">{t('promoters.form.pictureHint')}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.firstName')}</label>
              <input {...register('firstName')} className={inputClass(!!errors.firstName)} />
              {errors.firstName && (
                <p className="text-xs text-red-600 mt-1">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.lastName')}</label>
              <input {...register('lastName')} className={inputClass(!!errors.lastName)} />
              {errors.lastName && (
                <p className="text-xs text-red-600 mt-1">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.phone')}</label>
            <input
              {...register('phone')}
              type="tel"
              className={inputClass()}
              placeholder={t('promoters.editModal.phoneOptional')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.company')}</label>
            <input
              {...register('company')}
              className={inputClass()}
              placeholder={t('promoters.editModal.companyOptional')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.linkedin')}</label>
            <input
              {...register('linkedin')}
              className={inputClass()}
              placeholder={t('promoters.editModal.linkedinOptional')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoters.form.companyUrl')}</label>
            <input
              {...register('companyUrl', {
                onBlur: (e) => logoSuggestion.lookup(e.target.value),
              })}
              className={inputClass()}
              placeholder={t('promoters.editModal.companyUrlPlaceholder')}
            />
            <LogoSuggestionCard
              suggestion={logoSuggestion.suggestion}
              checking={logoSuggestion.checking}
              onApply={logoSuggestion.apply}
              onDismiss={logoSuggestion.dismiss}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoters.editModal.companyLogoLabel')}</label>
            <div className="flex items-center gap-3">
              <Avatar
                src={watch('companyLogo') || watch('avatar') || undefined}
                firstName={watch('firstName')}
                lastName={watch('lastName')}
                size="md"
              />
              <input
                {...register('companyLogo')}
                className={`${inputClass()} flex-1`}
                placeholder={t('promoters.editModal.companyLogoPlaceholder')}
              />
              {watch('companyLogo') && (
                <button
                  type="button"
                  onClick={() => setValue('companyLogo', '', { shouldDirty: true })}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  {t('promoters.editModal.clear')}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('promoters.editModal.companyLogoHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoters.form.companySize')}</label>
            <select {...register('companySize')} className={inputClass()}>
              <option value="">{t('promoters.editModal.selectSize')}</option>
              {COMPANY_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('promoters.editModal.industrySectors')} {selectedSectors.length > 0 && t('promoters.editModal.industrySectorsSelected', { count: selectedSectors.length })}
            </label>
            <button
              type="button"
              onClick={() => setSectorsOpen(!sectorsOpen)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#4339A0]"
            >
              <span className="text-gray-700">
                {selectedSectors.length > 0
                  ? selectedSectors.join(', ')
                  : t('promoters.editModal.selectSectors')}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${sectorsOpen ? 'rotate-180' : ''}`} />
            </button>
            {sectorsOpen && (
              <div className="mt-1 border border-gray-200 rounded-lg max-h-40 overflow-y-auto bg-white">
                {INDUSTRY_SECTORS.map((sector) => (
                  <label
                    key={sector}
                    className="flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSectors.includes(sector)}
                      onChange={() => toggleSector(sector)}
                      className="mr-2 rounded border-gray-300 text-[#4339A0] focus:ring-[#4339A0]"
                    />
                    {sector}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoters.editModal.timezone')}</label>
            <input
              {...register('timezone')}
              className={inputClass()}
              placeholder={t('promoters.editModal.timezonePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('promoters.editModal.referenceCallFrequency')}</label>
            <input
              {...register('referenceCallFrequency')}
              className={inputClass()}
              placeholder={t('promoters.editModal.referenceCallFrequencyPlaceholder')}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || !isDirty}
              className="px-4 py-2 text-sm font-medium text-white bg-[#4339A0] rounded-lg hover:bg-[#362d85] transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{t('common.saveChanges')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPromoterModal;
